import { useCallback, useEffect, useState } from 'react';
import {
  generatePositions, listPositions, previewScheduleRefresh, refreshSchedulePositions,
  setPositionRate,
  type PositionWithPdv, type ScheduleRefreshPreview,
} from '../../api/positions';
import { listSchedules, type ScheduleWithPdv } from '../../api/campaigns';
import type { CampaignRow } from '../../lib/db-types';
import FilaTarjeta from '../../components/FilaTarjeta';

const STATUS_LABEL: Record<string, string> = {
  abierta: 'Abierta', asignada: 'Asignada', cerrada: 'Cerrada',
};

export default function CampaniaPosiciones({ campaign, soloLectura = false }: {
  campaign: CampaignRow;
  /** Generar y regenerar posiciones es escribir la programación: desde la migración 0019 la
   *  RLS solo se lo permite al comercial dueño y al jefe. El coordinador y el supervisor
   *  entran a leer cuántas posiciones hay y en qué estado. */
  soloLectura?: boolean;
}) {
  const [positions, setPositions] = useState<PositionWithPdv[]>([]);
  const [schedules, setSchedules] = useState<ScheduleWithPdv[]>([]);
  const [dateFrom, setDateFrom] = useState(campaign.start_date);
  const [dateTo, setDateTo] = useState(campaign.end_date);
  const [pdvId, setPdvId] = useState('');
  const [loading, setLoading] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  // Regeneración de un horario editado
  const [refreshTarget, setRefreshTarget] = useState<ScheduleWithPdv | null>(null);
  /** Jornada cuya tarifa se está corrigiendo. `null` = el modal está cerrado. */
  const [tarifa, setTarifa] = useState<
    { id: string; monto: string; unidad: 'jornada' | 'hora'; motivo: string; etiqueta: string } | null
  >(null);
  const [tarifaError, setTarifaError] = useState<string | null>(null);
  const [guardandoTarifa, setGuardandoTarifa] = useState(false);
  const [refreshPreview, setRefreshPreview] = useState<ScheduleRefreshPreview | null>(null);
  const [refreshing, setRefreshing] = useState(false);
  const [refreshError, setRefreshError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setPositions(await listPositions({
        campaignId: campaign.id,
        dateFrom: dateFrom || undefined,
        dateTo: dateTo || undefined,
        pdvId: pdvId || undefined,
      }));
    } catch (e) {
      setError(e instanceof Error ? e.message : 'No se pudieron cargar las posiciones');
    } finally {
      setLoading(false);
    }
  }, [campaign.id, dateFrom, dateTo, pdvId]);

  useEffect(() => { void load(); }, [load]);

  useEffect(() => {
    listSchedules(campaign.id)
      .then(setSchedules)
      .catch(() => setSchedules([]));
  }, [campaign.id]);

  // PDV únicos de los horarios de la campaña (para el filtro)
  const pdvOptions = Array.from(
    new Map(schedules.map((s) => [s.pdv_id, `${s.pdv.code} — ${s.pdv.name}`])).entries(),
  );

  async function generate() {
    setGenerating(true);
    setMessage(null);
    setError(null);
    try {
      const { inserted, expanded } = await generatePositions(campaign.id);
      if (expanded === 0) {
        setMessage('La campaña no tiene horarios cargados: no hay nada que generar.');
      } else if (inserted === 0) {
        setMessage(`0 posiciones nuevas creadas (las ${expanded} de la programación ya existían).`);
      } else {
        setMessage(`${inserted} posiciones nuevas creadas (de ${expanded} de la programación).`);
      }
      await load();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'No se pudieron generar las posiciones');
    } finally {
      setGenerating(false);
    }
  }

  /** Abre el panel de regeneración con el impacto real ya calculado. */
  async function openRefresh(s: ScheduleWithPdv) {
    setRefreshTarget(s);
    setRefreshPreview(null);
    setRefreshError(null);
    setMessage(null);
    try {
      setRefreshPreview(await previewScheduleRefresh(s.id));
    } catch (e) {
      setRefreshError(e instanceof Error ? e.message : 'No se pudo calcular el impacto de la regeneración');
    }
  }

  async function confirmRefresh() {
    if (!refreshTarget) return;
    setRefreshing(true);
    setRefreshError(null);
    try {
      const r = await refreshSchedulePositions(refreshTarget.id);
      const partes = [
        `${r.updated} posiciones abiertas actualizadas`,
        `${r.deleted} eliminadas por ya no corresponder a los días del horario`,
        `${r.inserted} creadas`,
      ];
      setMessage(
        `Horario de ${refreshTarget.pdv.name} regenerado: ${partes.join(', ')}.` +
        (r.kept > 0
          ? ` ${r.kept} que el horario ya no pide se conservaron porque tienen alguna postulación (aunque sea rechazada).`
          : '') +
        (r.locked > 0
          ? ` Quedaron ${r.locked} posiciones con recurso (asignada/cerrada) SIN cambios: requieren decisión explícita (reasignar o cerrar) antes de que su horario o tarifa coincidan con el nuevo.`
          : ''),
      );
      setRefreshTarget(null);
      setRefreshPreview(null);
      await load();
    } catch (e) {
      setRefreshError(e instanceof Error ? e.message : 'No se pudo regenerar el horario');
    } finally {
      setRefreshing(false);
    }
  }

  return (
    <section className="border rounded p-4 bg-white">
      <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
        <h2 className="font-semibold">Posiciones</h2>
        {!soloLectura && (
          <button
            className="px-3 py-2 bg-blue-600 text-white rounded disabled:opacity-50"
            disabled={generating}
            onClick={() => void generate()}
          >
            {generating ? 'Generando…' : 'Generar posiciones'}
          </button>
        )}
      </div>
      <p className="text-sm text-gray-600 mb-3">
        {soloLectura
          ? `Los puestos generados de esta campaña (PDV × fecha × recurso) entre ${campaign.start_date} y ${campaign.end_date}. Es lo que hay que cubrir.`
          : `Expande los horarios de la campaña (PDV × fecha × recurso) dentro de ${campaign.start_date} al ${campaign.end_date}. Se puede repetir: las posiciones que ya existen no se duplican.`}
      </p>

      {message && <p className="text-green-700 mb-2">{message}</p>}
      {error && <p className="text-red-600 mb-2">{error}</p>}

      {schedules.length > 0 && !soloLectura && (
        <div className="border rounded p-3 mb-3 bg-gray-50">
          <h3 className="font-semibold text-sm mb-1">Regenerar un horario editado</h3>
          <p className="text-sm text-gray-600 mb-2">
            Guardar un horario en la pestaña Horarios ya ajusta sus posiciones solo. "Regenerar" hace lo
            mismo a pedido: crea las que el horario pide y faltan, quita las abiertas sin persona que ya no
            pide, pone al día la hora y la tarifa, y te dice cuáles ya tienen recurso asignado y necesitan
            tu decisión.
          </p>
          <ul className="text-sm">
            {schedules.map((s) => (
              <li key={s.id} className="flex items-center justify-between border-t py-1">
                <span>
                  {s.pdv.code} — {s.pdv.name} · {s.start_time.slice(0, 5)}–{s.end_time.slice(0, 5)} ·
                  {' '}S/ {Number(s.rate_amount).toFixed(2)} / {s.rate_unit}
                </span>
                <button className="text-blue-600 underline" onClick={() => void openRefresh(s)}>
                  Regenerar horario
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}

      <div className="flex flex-wrap gap-3 mb-3">
        <label className="text-sm">Desde
          <input type="date" className="border rounded px-2 py-1 block"
            value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} />
        </label>
        <label className="text-sm">Hasta
          <input type="date" className="border rounded px-2 py-1 block"
            value={dateTo} onChange={(e) => setDateTo(e.target.value)} />
        </label>
        {/* min-w-0 + max-w-full: en celular un nombre de PDV largo hacía al select más ancho
            que la pantalla y desplazaba toda la página. En escritorio sobra sitio: no cambia. */}
        <label className="text-sm min-w-0">PDV
          <select className="border rounded px-2 py-1 block max-w-full"
            value={pdvId} onChange={(e) => setPdvId(e.target.value)}>
            <option value="">— Todos —</option>
            {pdvOptions.map(([id, label]) => <option key={id} value={id}>{label}</option>)}
          </select>
        </label>
      </div>

      <p className="text-sm text-gray-700 mb-2">{positions.length} posiciones en el filtro actual</p>

      {/* En celular: una tarjeta por posición (sin desplazamiento horizontal). */}
      <div className="space-y-2 lg:hidden">
        {loading && <p className="border rounded p-4 text-center text-gray-500">Cargando…</p>}
        {!loading && positions.length === 0 && (
          <p className="border rounded p-4 text-center text-gray-500">Sin posiciones para este filtro</p>
        )}
        {positions.map((p) => (
          <FilaTarjeta
            key={p.id}
            titulo={`${p.pdv.code} — ${p.pdv.name}`}
            campos={[
              { etiqueta: 'Fecha', valor: p.date },
              { etiqueta: 'Horario', valor: `${p.start_time.slice(0, 5)}–${p.end_time.slice(0, 5)}` },
              { etiqueta: 'Slot', valor: p.slot_index },
              { etiqueta: 'Tarifa', valor: `S/ ${Number(p.rate_amount).toFixed(2)} / ${p.rate_unit}` },
              { etiqueta: 'Estado', valor: STATUS_LABEL[p.status] ?? p.status },
            ]}
          />
        ))}
      </div>

      <div className="hidden overflow-x-auto lg:block">
        <table className="w-full text-sm border">
          <thead>
            <tr className="bg-gray-100 text-left">
              <th className="p-2">Fecha</th><th className="p-2">PDV</th><th className="p-2">Horario</th>
              <th className="p-2">Slot</th><th className="p-2">Tarifa</th><th className="p-2">Estado</th>
            </tr>
          </thead>
          <tbody>
            {loading && <tr><td className="p-2" colSpan={6}>Cargando…</td></tr>}
            {!loading && positions.length === 0 && (
              <tr><td className="p-2" colSpan={6}>Sin posiciones para este filtro</td></tr>
            )}
            {positions.map((p) => (
              <tr key={p.id} className="border-t">
                <td className="p-2">{p.date}</td>
                <td className="p-2">{p.pdv.code} — {p.pdv.name}</td>
                <td className="p-2">{p.start_time.slice(0, 5)}–{p.end_time.slice(0, 5)}</td>
                <td className="p-2">{p.slot_index}</td>
                <td className="p-2">
                  S/ {Number(p.rate_amount).toFixed(2)} / {p.rate_unit}
                  {!soloLectura && (
                    <button
                      type="button"
                      onClick={() => { setTarifa({ id: p.id, monto: String(p.rate_amount), unidad: p.rate_unit, motivo: '', etiqueta: `${p.date} · ${p.pdv.name}` }); setTarifaError(null); }}
                      className="ml-2 text-xs text-blue-700 underline"
                    >
                      cambiar
                    </button>
                  )}
                </td>
                <td className="p-2">{STATUS_LABEL[p.status] ?? p.status}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {tarifa && (
        <div className="fixed inset-0 flex items-center justify-center bg-black/40 p-4">
          <div className="w-full max-w-sm rounded bg-white p-4">
            <h3 className="mb-1 font-semibold">Cambiar la tarifa de esta jornada</h3>
            <p className="mb-3 text-sm text-gray-600">{tarifa.etiqueta}</p>
            {tarifaError && (
              <p className="mb-2 rounded border border-red-200 bg-red-50 p-2 text-sm text-red-700">
                {tarifaError}
              </p>
            )}
            <label className="block text-sm">
              Tarifa (S/)
              <input
                value={tarifa.monto}
                onChange={(e) => setTarifa({ ...tarifa, monto: e.target.value })}
                inputMode="decimal"
                className="mt-1 w-full rounded border px-2 py-1"
              />
            </label>
            <label className="mt-2 block text-sm">
              Unidad
              <select
                value={tarifa.unidad}
                onChange={(e) => setTarifa({ ...tarifa, unidad: e.target.value as 'jornada' | 'hora' })}
                className="mt-1 w-full rounded border px-2 py-1"
              >
                <option value="jornada">Por jornada</option>
                <option value="hora">Por hora</option>
              </select>
            </label>
            <label className="mt-2 block text-sm">
              Motivo <span className="text-red-600">*</span> (queda en la auditoría)
              <input
                value={tarifa.motivo}
                onChange={(e) => setTarifa({ ...tarifa, motivo: e.target.value })}
                className="mt-1 w-full rounded border px-2 py-1"
              />
            </label>
            {/* Se dice ANTES de guardar, no después: cambiar la tarifa es cambiar lo que se le
                paga a una persona por ese día. */}
            <p className="mt-2 text-xs text-gray-500">
              Solo afecta a esta jornada. Si ya está en un cierre aprobado, el sistema lo
              rechaza: eso se corrige por ajuste.
            </p>
            <div className="mt-4 flex justify-end gap-2">
              <button className="rounded bg-gray-200 px-3 py-2" onClick={() => setTarifa(null)}>
                Cancelar
              </button>
              <button
                className="rounded bg-blue-600 px-3 py-2 text-white disabled:opacity-50"
                disabled={guardandoTarifa}
                onClick={() => void (async () => {
                  setTarifaError(null);
                  const monto = Number(tarifa.monto);
                  if (!Number.isFinite(monto) || monto <= 0) {
                    setTarifaError('La tarifa tiene que ser un número mayor que cero.');
                    return;
                  }
                  // El motivo es obligatorio (0039). Una tarifa distinta a la programada es una
                  // excepción que alguien va a tener que explicar en el cierre —"se pagó más
                  // para conseguir un reemplazo"—, y sin la razón escrita en el momento nadie la
                  // reconstruye tres semanas después. Se exige acá además de en el servidor para
                  // que se pida cuando todavía se está pensando en el cambio.
                  if (tarifa.motivo.trim() === '') {
                    setTarifaError('Escribe por qué cambia la tarifa: queda en la auditoría.');
                    return;
                  }
                  setGuardandoTarifa(true);
                  try {
                    await setPositionRate(tarifa.id, monto, tarifa.unidad, tarifa.motivo.trim());
                    setTarifa(null);
                    await load();
                  } catch (e) {
                    setTarifaError(e instanceof Error ? e.message : 'No se pudo cambiar la tarifa.');
                  } finally {
                    setGuardandoTarifa(false);
                  }
                })()}
              >
                {guardandoTarifa ? 'Guardando…' : 'Guardar'}
              </button>
            </div>
          </div>
        </div>
      )}

      {refreshTarget && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center p-4">
          <div className="bg-white rounded p-4 w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <h3 className="font-semibold mb-2">
              Regenerar el horario de {refreshTarget.pdv.code} — {refreshTarget.pdv.name}
            </h3>
            {refreshError && <p className="text-red-600 mb-2">{refreshError}</p>}
            {!refreshPreview && !refreshError && <p className="text-gray-500">Calculando impacto…</p>}
            {refreshPreview && (
              <div className="text-sm">
                <ul className="list-disc pl-5 mb-3">
                  <li>{refreshPreview.updatable} posiciones abiertas quedarán con la hora, la tarifa y el PDV actuales del horario.</li>
                  <li>{refreshPreview.obsolete} posiciones abiertas se eliminarán por caer en días que el horario ya no cubre o fuera de su vigencia.</li>
                  <li>{refreshPreview.missing} posiciones nuevas se crearán para los días que faltan.</li>
                </ul>
                {refreshPreview.locked.length > 0 && (
                  <div className="border border-orange-300 bg-orange-50 rounded p-2 mb-3">
                    <p className="font-semibold text-orange-800 mb-1">
                      {refreshPreview.locked.length} posiciones NO se tocarán porque ya tienen recurso:
                      decide una por una si reasignas o cierras.
                    </p>
                    <ul className="list-disc pl-5 text-orange-900 max-h-40 overflow-y-auto">
                      {refreshPreview.locked.map((p) => (
                        <li key={p.id}>
                          {p.date} · {p.startTime}–{p.endTime} · S/ {p.rateAmount.toFixed(2)} / {p.rateUnit}
                          {' '}· {STATUS_LABEL[p.status] ?? p.status}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            )}
            <div className="flex gap-2 justify-end">
              <button
                className="px-3 py-2 bg-gray-200 rounded"
                disabled={refreshing}
                onClick={() => { setRefreshTarget(null); setRefreshPreview(null); setRefreshError(null); }}
              >
                Cancelar
              </button>
              <button
                className="px-3 py-2 bg-blue-600 text-white rounded disabled:opacity-50"
                disabled={refreshing || refreshPreview === null}
                onClick={() => void confirmRefresh()}
              >
                {refreshing ? 'Regenerando…' : 'Regenerar'}
              </button>
            </div>
          </div>
        </div>
      )}
    </section>
  );
}
